import React, { useCallback, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { Col, Grid, Row } from 'react-styled-flexboxgrid';
import { useTheme } from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { FormGroup, Input } from '@savgroup-front-common/core/atoms/Form';
import { Heading } from '@savgroup-front-common/core/atoms/Heading';
import SwipeableDrawer, {
  DRAWER_SIZE,
} from '@savgroup-front-common/core/atoms/SwipeableDrawer/SwipeableDrawer';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { rem } from '@savgroup-front-common/primitive/helpers/units';
import { FLEX_ALIGN } from '@savgroup-front-common/types';

import useSellerSelfcareConfiguration from '@/hooks/useSellerSelfcareConfiguration';
import { CreateClaimValues } from '@/types';
import StepLayout from '@/view/app/layout/NewDesignLayout/StepLayout/StepLayout';
import STEP_CONFIG from '@/view/app/Routes/StepConfig';

import messagesForm from './messages';
import useOnlyWithOrderReference from './OnlyWithOrderReference.hooks';
import {
  $HelperButton,
  $HelperContainerTitle,
  $Img,
} from './OnlyWithOrderReference.styles';

interface OnlyWithOrderReferenceProps {
  values: CreateClaimValues;
}

const OnlyWithOrderReference: React.FC<OnlyWithOrderReferenceProps> = ({
  values,
}) => {
  const [isHelperOpen, setIsOpenHelper] = useState<boolean>(false);
  const theme = useTheme();
  const { formContext, isLoading, onSubmit } = useOnlyWithOrderReference({
    values,
  });
  const { sellerConfiguration } = useSellerSelfcareConfiguration({
    sellerId: values.sellerId,
  });

  const {
    formState: { errors, isValid },
    control,
    handleSubmit,
  } = formContext;

  const handleHelper = useCallback(() => {
    setIsOpenHelper((prev) => !prev);
  }, []);

  return (
    <FormProvider {...formContext}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StepLayout
          values={values}
          stepName={STEP_CONFIG.CUSTOMER_INVOICE_INFORMATION}
          isDisabled={!isValid}
          isLoading={isLoading}
        >
          <Grid fluid>
            <Row>
              <Col sm={12} xs={12}>
                <FormGroup>
                  <Controller
                    control={control}
                    name="orderReference"
                    render={({ field }) => {
                      return (
                        <Input
                          name={field.name}
                          value={field.value}
                          label={messagesForm.orderReferenceLabel}
                          disableBrowserAutocomplete
                          onChange={field.onChange}
                          errors={errors}
                          postLabel={
                            sellerConfiguration?.orderHelpText &&
                            sellerConfiguration?.orderHelpImageUrls?.length >
                              0 ? (
                              <$HelperButton
                                hollow
                                naked
                                underline
                                type={BUTTON_TYPES.BUTTON}
                                onClick={handleHelper}
                              >
                                <SafeFormattedMessageWithoutSpread
                                  message={
                                    messagesForm.orderReferenceHelperLabel
                                  }
                                />
                              </$HelperButton>
                            ) : undefined
                          }
                          fullWidthLabel
                          isRequired
                        />
                      );
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Grid>
        </StepLayout>
      </form>
      {sellerConfiguration && (
        <SwipeableDrawer
          isOpen={isHelperOpen}
          size={DRAWER_SIZE.SMALL}
          onClose={() => setIsOpenHelper(false)}
          hasBackdrop
          hasBackdropScroll={false}
        >
          <$HelperContainerTitle>
            <Heading
              level={2}
              align={FLEX_ALIGN.LEFT}
              color={theme.colors.black}
            >
              <SafeFormattedMessageWithoutSpread
                message={messagesForm.orderReferenceHelperTitle}
              />
            </Heading>
          </$HelperContainerTitle>
          <SafeFormattedMessageWithoutSpread
            message={sellerConfiguration?.orderHelpText}
          />
          {sellerConfiguration?.orderHelpImageUrls.map((url) => {
            return (
              <div key={url} style={{ marginTop: rem(28) }}>
                <$Img src={url} alt="helperImage" />
              </div>
            );
          })}
        </SwipeableDrawer>
      )}
    </FormProvider>
  );
};

OnlyWithOrderReference.displayName = 'OnlyWithOrderReference';

export default OnlyWithOrderReference;
